import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  EyeIcon,
  CloudIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  CogIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  ClipboardIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

interface BrandingSettings {
  logo_url?: string;
  logo_alt_text?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  background_color?: string;
  text_color?: string;
  font_family?: string;
  custom_css?: string;
  favicon_url?: string;
  og_image_url?: string;
  og_title?: string;
  og_description?: string;
  company_name?: string;
  company_description?: string;
  remove_branding?: boolean;
  custom_domain?: string;
  domain_verified?: boolean;
  sender_domain?: string;
  sender_domain_verified?: boolean;
  spf_verified?: boolean;
  dkim_verified?: boolean;
  dmarc_verified?: boolean;
  cname_verified?: boolean;
  custom_domain_enabled?: boolean;
}

interface DNSHealthCheck {
  domain: string;
  overall_status: 'healthy' | 'warning' | 'error';
  spf_status: DNSRecord;
  dkim_status: DNSRecord;
  dmarc_status: DNSRecord;
  cname_status: DNSRecord;
  domain_verified: boolean;
  sender_domain_verified: boolean;
  fully_verified: boolean;
  recommendations: string[];
  last_checked?: string;
}

interface DNSRecord {
  domain: string;
  record_type: string;
  verified: boolean;
  record_found: boolean;
  record_value?: string;
  expected_value?: string;
  errors: string[];
  last_checked?: string;
}

interface DomainVerification {
  domain: string;
  verification_type: string;
  verification_token: string;
  verification_url: string;
  dns_records: Record<string, any[]>;
  instructions: string[];
}

const BrandingWizard: React.FC = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [brandingData, setBrandingData] = useState<BrandingSettings>({});
  const [domainToVerify, setDomainToVerify] = useState('');
  const [verificationType, setVerificationType] = useState<'domain' | 'sender'>('domain');
  const [previewType, setPreviewType] = useState<'email' | 'web' | 'sms'>('email');
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [verificationData, setVerificationData] = useState<DomainVerification | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  const { user, tenant } = useAuth();

  // Fetch feature availability
  const { data: features, isLoading: featuresLoading, error: featuresError } = useQuery({
    queryKey: ['features', tenant?.id], // Include tenant ID to match MainLayout and refetch when tenant changes
    queryFn: async () => {
      const response = await api.billing.getFeatures();
      // Response structure: { data: { plan: string, plan_level: number, features: {...} } }
      const featuresData = response.data?.features || {};
      console.debug('BrandingWizard - Features loaded:', featuresData, 'Full response:', response.data);
      return featuresData;
    },
    enabled: !!user && !!tenant, // Only fetch if user and tenant exist
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  // Log feature errors and loading state for debugging
  useEffect(() => {
    if (featuresError) {
      console.error('BrandingWizard - Error loading features:', featuresError);
    }
    if (featuresLoading) {
      console.debug('BrandingWizard - Loading features...');
    }
    if (features) {
      console.debug('BrandingWizard - Features state:', features, 'white_label_branding:', features.white_label_branding);
    }
  }, [featuresError, featuresLoading, features]);

  // Fetch current branding settings
  const { data: currentBranding, isLoading: brandingLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: async () => {
      const response = await api.branding.get();
      return response.data;
    },
    enabled: features?.white_label_branding !== false
  });

  // Fetch wizard status
  const { data: wizardStatus, refetch: refetchWizardStatus } = useQuery({
    queryKey: ['wizard-status'],
    queryFn: async () => {
      const response = await api.branding.getWizardStatus();
      return response.data;
    }
  });

  // Fetch DNS health check
  const { data: dnsHealth, refetch: refetchDNS } = useQuery({
    queryKey: ['dns-health'],
    queryFn: async () => {
      const response = await api.branding.getDNSHealth();
      return response.data;
    },
    enabled: activeStep === 5 || activeStep === 6
  });

  // Save wizard step mutation
  const saveWizardStepMutation = useMutation({
    mutationFn: async ({ step, data }: { step: number; data: any }) => {
      const response = await api.branding.saveWizardStep(step, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      queryClient.invalidateQueries({ queryKey: ['wizard-status'] });
      refetchWizardStatus();
      if (variables.step === 1) {
        setBrandingSaved(true);
      }
      toast.success('Settings saved successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save settings. Please try again.';
      toast.error('Failed to save settings', {
        description: errorMessage
      });
    }
  });

  // Update branding mutation (for backward compatibility)
  const updateBrandingMutation = useMutation({
    mutationFn: async (data: BrandingSettings) => {
      const response = await api.branding.update(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      queryClient.invalidateQueries({ queryKey: ['wizard-status'] });
      refetchWizardStatus();
      toast.success('Branding settings updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to update branding settings. Please try again.';
      toast.error('Failed to update settings', {
        description: errorMessage
      });
    }
  });

  // Start domain verification mutation
  const startVerificationMutation = useMutation({
    mutationFn: async ({ domain, verification_type }: { domain: string; verification_type: string }) => {
      const response = await api.branding.verifyDomainStart(domain, verification_type);
      return response.data;
    },
    onSuccess: (data) => {
      // Store verification data to display DNS records and instructions
      setVerificationData(data);
      toast.success('Verification started successfully!', {
        description: 'Please add the DNS records shown below to your domain. DNS changes may take a few minutes to propagate.'
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to start verification. Please try again.';
      toast.error('Failed to start verification', {
        description: errorMessage
      });
    }
  });

  // Verify domain mutation
  const verifyDomainMutation = useMutation({
    mutationFn: async ({ domain, verification_type }: { domain: string; verification_type: string }) => {
      const response = await api.branding.verifyDomainCheck(domain, verification_type);
      return response.data;
    },
    onSuccess: (data) => {
      refetchDNS();
      if (data?.verified) {
        toast.success('Domain verified successfully!', {
          description: 'Your domain has been verified and is now active.'
        });
      } else {
        const errors = data?.errors || [];
        const errorMessage = errors.length > 0 
          ? errors.join('. ') 
          : 'Domain not yet verified. Please ensure DNS records are added correctly and wait a few minutes for DNS propagation.';
        toast.warning('Domain not verified', {
          description: errorMessage
        });
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to verify domain. Please try again.';
      toast.error('Verification check failed', {
        description: errorMessage
      });
    }
  });

  // Generate preview mutation
  const previewMutation = useMutation({
    mutationFn: async ({ branding_data, preview_type }: { branding_data: BrandingSettings; preview_type: string }) => {
      const response = await api.branding.preview({ branding_data, preview_type });
      return response.data;
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to generate preview. Please try again.';
      toast.error('Preview generation failed', {
        description: errorMessage
      });
    }
  });

  useEffect(() => {
    if (currentBranding) {
      setBrandingData(currentBranding);
    }
  }, [currentBranding]);

  // Set active step from wizard status, but cap at step 1 for Growth subscribers
  useEffect(() => {
    if (wizardStatus && wizardStatus.current_step) {
      // Growth subscribers (without custom_domain) should only see step 1
      const maxStep = features?.custom_domain ? 6 : 1;
      setActiveStep(Math.min(wizardStatus.current_step, maxStep));
    }
  }, [wizardStatus, features]);

  // Ensure activeStep never exceeds allowed steps based on plan level
  useEffect(() => {
    const maxStep = features?.custom_domain ? 6 : 1;
    if (activeStep > maxStep) {
      setActiveStep(maxStep);
    }
  }, [activeStep, features]);

  const handleInputChange = (field: keyof BrandingSettings, value: string | boolean) => {
    setBrandingData(prev => ({ ...prev, [field]: value }));
    setBrandingSaved(false);
  };

  const handleSaveStep = (step: number) => {
    let stepData: any = {};
    
    if (step === 1) {
      stepData = {
        logo_url: brandingData.logo_url,
        logo_alt_text: brandingData.logo_alt_text,
        primary_color: brandingData.primary_color,
        secondary_color: brandingData.secondary_color,
        accent_color: brandingData.accent_color,
        background_color: brandingData.background_color,
        text_color: brandingData.text_color,
        font_family: brandingData.font_family,
        custom_css: brandingData.custom_css,
        favicon_url: brandingData.favicon_url,
        og_image_url: brandingData.og_image_url,
        og_title: brandingData.og_title,
        og_description: brandingData.og_description,
        company_name: brandingData.company_name,
        company_description: brandingData.company_description,
        remove_branding: brandingData.remove_branding
      };
    } else if (step === 2) {
      stepData = { custom_domain: brandingData.custom_domain };
    } else if (step === 4) {
      stepData = { sender_domain: brandingData.sender_domain };
    } else if (step === 6) {
      stepData = { custom_domain_enabled: brandingData.custom_domain_enabled };
    }
    
    saveWizardStepMutation.mutate({ step, data: stepData });
  };

  const handleSave = () => {
    handleSaveStep(activeStep);
  };

  const handleStartVerification = () => {
    if (domainToVerify) {
      setVerificationData(null); // Clear previous data
      startVerificationMutation.mutate({
        domain: domainToVerify,
        verification_type: verificationType
      });
    }
  };

  const handleCopyToClipboard = async (text: string, recordKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(recordKey);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleVerifyDomain = () => {
    if (domainToVerify) {
      verifyDomainMutation.mutate({
        domain: domainToVerify,
        verification_type: verificationType
      });
    }
  };

  const handleGeneratePreview = () => {
    previewMutation.mutate({
      branding_data: brandingData,
      preview_type: previewType
    });
  };

  const getStatusIcon = (verified: boolean) => {
    if (verified) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    return <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getOverallStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Determine available steps based on plan level
  const allSteps = [
    { id: 1, name: 'Branding', icon: EyeIcon },
    { id: 2, name: 'Domain Setup', icon: GlobeAltIcon },
    { id: 3, name: 'DNS Verification', icon: ShieldCheckIcon },
    { id: 4, name: 'Sender Domain', icon: EnvelopeIcon },
    { id: 5, name: 'Health Check', icon: CogIcon },
    { id: 6, name: 'Activation', icon: CheckBadgeIcon }
  ];
  
  // Growth subscribers only see step 1, Agency subscribers see all 6 steps
  const steps = features?.custom_domain ? allSteps : allSteps.slice(0, 1);

  if (featuresLoading || brandingLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show loading state while features are being fetched
  if (featuresLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <p className="text-gray-600">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  // Check if white-label branding is available
  // Check explicitly for true (not just truthy) to avoid showing upgrade message incorrectly
  if (!features || features.white_label_branding !== true) {
    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Branding & Domain Setup
            </h2>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-gray-700 mb-4">
              <strong>White-Label Branding</strong> is available on <strong>Growth+</strong> plans.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Upgrade to customize your branding, configure colors, logos, and white-label settings.
            </p>
            <a
              href="/billing"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Upgrade to Growth Plan
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Branding & Domain Setup</h1>
        <p className="mt-2 text-gray-600">Customize your brand and configure domain verification</p>
      </div>
      
      {/* Custom Domain Upgrade Notice */}
      {features && !features.custom_domain && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800">
                <strong>Custom domains</strong> are only available on the <strong>Agency</strong> plan. 
                <a href="/billing" className="text-blue-600 underline ml-1">Upgrade to Agency</a> to use custom domains.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      {steps.length > 1 && (
        <div className="mb-8">
          <nav className="flex space-x-8">
            {steps.map((step) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  activeStep >= step.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  <step.icon className="h-5 w-5" />
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  activeStep >= step.id ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {step.name}
                </span>
              </div>
            ))}
          </nav>
        </div>
      )}

      {/* Step 1: Branding */}
      {activeStep === 1 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Branding Configuration</h2>
          <p className="text-gray-600">Configure your company branding, colors, and visual identity.</p>
          
          <div className="space-y-6">
            {/* Company Information */}
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-4">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={brandingData.company_name || ''}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Company Name"
                  />
                  <p className="mt-1 text-xs text-gray-500">This replaces "FeedbackGate" in all customer-facing content</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remove FeedbackGate Branding
                    {features && !features.custom_domain && (
                      <span className="ml-2 text-xs text-yellow-600">(Agency plan required)</span>
                    )}
                  </label>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={brandingData.remove_branding || false}
                      onChange={(e) => handleInputChange('remove_branding', e.target.checked)}
                      disabled={features && !features.custom_domain}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <label className={`ml-2 text-sm ${features && !features.custom_domain ? 'text-gray-500' : 'text-gray-700'}`}>
                      Remove all FeedbackGate branding from customer-facing content
                    </label>
                  </div>
                  {features && !features.custom_domain && (
                    <p className="mt-1 text-xs text-yellow-600">
                      This feature requires an Agency plan. <a href="/billing" className="text-blue-600 underline">Upgrade to Agency</a> to remove FeedbackGate branding.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Description
                </label>
                <textarea
                  value={brandingData.company_description || ''}
                  onChange={(e) => handleInputChange('company_description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of your company"
                />
              </div>
            </div>
            
            {/* Visual Branding */}
            <div>
              <h3 className="font-medium text-gray-900 mb-4">Visual Branding</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={brandingData.logo_url || ''}
                    onChange={(e) => handleInputChange('logo_url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/logo.png"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Alt Text
                  </label>
                  <input
                    type="text"
                    value={brandingData.logo_alt_text || ''}
                    onChange={(e) => handleInputChange('logo_alt_text', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Company Logo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={brandingData.primary_color || '#007bff'}
                      onChange={(e) => handleInputChange('primary_color', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={brandingData.primary_color || ''}
                      onChange={(e) => handleInputChange('primary_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#007bff"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={brandingData.secondary_color || '#6c757d'}
                      onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={brandingData.secondary_color || ''}
                      onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#6c757d"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accent Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={brandingData.accent_color || '#28a745'}
                      onChange={(e) => handleInputChange('accent_color', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={brandingData.accent_color || ''}
                      onChange={(e) => handleInputChange('accent_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#28a745"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Family
                  </label>
                  <select
                    value={brandingData.font_family || ''}
                    onChange={(e) => handleInputChange('font_family', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Font</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Helvetica, sans-serif">Helvetica</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Times New Roman, serif">Times New Roman</option>
                    <option value="Inter, sans-serif">Inter</option>
                    <option value="Roboto, sans-serif">Roboto</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={brandingData.background_color || '#ffffff'}
                      onChange={(e) => handleInputChange('background_color', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={brandingData.background_color || ''}
                      onChange={(e) => handleInputChange('background_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Color
                  </label>
                  <div className="flex">
                    <input
                      type="color"
                      value={brandingData.text_color || '#333333'}
                      onChange={(e) => handleInputChange('text_color', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded-l-md"
                    />
                    <input
                      type="text"
                      value={brandingData.text_color || ''}
                      onChange={(e) => handleInputChange('text_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#333333"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-4">Additional Branding</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Favicon URL
                    </label>
                    <input
                      type="url"
                      value={brandingData.favicon_url || ''}
                      onChange={(e) => handleInputChange('favicon_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/favicon.ico"
                    />
                    <p className="mt-1 text-xs text-gray-500">Icon shown in browser tabs</p>
                  </div>

                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Open Graph settings are currently not in use. These will be available for future public-facing pages (e.g., customer portals, public landing pages).
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Open Graph Image URL
                    </label>
                    <input
                      type="url"
                      value={brandingData.og_image_url || ''}
                      onChange={(e) => handleInputChange('og_image_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/og-image.png"
                    />
                    <p className="mt-1 text-xs text-gray-500">Image shown when sharing links on social media (currently not in use)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Open Graph Title
                    </label>
                    <input
                      type="text"
                      value={brandingData.og_title || ''}
                      onChange={(e) => handleInputChange('og_title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your Company Name"
                    />
                    <p className="mt-1 text-xs text-gray-500">Title shown when sharing links on social media (currently not in use)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Open Graph Description
                    </label>
                    <textarea
                      value={brandingData.og_description || ''}
                      onChange={(e) => handleInputChange('og_description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief description of your company"
                    />
                    <p className="mt-1 text-xs text-gray-500">Description shown when sharing links on social media (currently not in use)</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom CSS
                </label>
                <textarea
                  value={brandingData.custom_css || ''}
                  onChange={(e) => handleInputChange('custom_css', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="/* Custom CSS styles */"
                />
                <p className="mt-1 text-xs text-gray-500">Custom CSS will be applied to emails and web pages</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saveWizardStepMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saveWizardStepMutation.isPending ? 'Saving...' : (brandingSaved ? 'Branding Saved' : 'Save Branding')}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Domain Setup - Only visible to Agency subscribers */}
      {activeStep === 2 && features?.custom_domain && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Custom Domain Setup</h2>
          <p className="text-gray-600">Configure your custom domain for white-label branding.</p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Domain
              {features && !features.custom_domain && (
                <span className="ml-2 text-xs text-yellow-600">(Agency plan required)</span>
              )}
            </label>
            <input
              type="text"
              value={brandingData.custom_domain || ''}
              onChange={(e) => handleInputChange('custom_domain', e.target.value)}
              disabled={features && !features.custom_domain}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="example.com"
            />
            <p className="mt-1 text-xs text-gray-500">Enter your custom domain (e.g., feedback.yourcompany.com)</p>
            {brandingData.custom_domain && (
              <div className="mt-2 flex items-center">
                {getStatusIcon(brandingData.domain_verified || false)}
                <span className="ml-2 text-sm text-gray-600">
                  {brandingData.domain_verified ? 'Verified' : 'Not verified'}
                </span>
              </div>
            )}
          </div>

          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Domain Verification</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain to Verify
                  {features && !features.custom_domain && (
                    <span className="ml-2 text-xs text-yellow-600">(Agency plan required)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={domainToVerify}
                  onChange={(e) => setDomainToVerify(e.target.value)}
                  disabled={features && !features.custom_domain}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Type
                </label>
                <select
                  value={verificationType}
                  onChange={(e) => setVerificationType(e.target.value as 'domain' | 'sender')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="domain">Custom Domain</option>
                  <option value="sender">Sender Domain</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleStartVerification}
                  disabled={!domainToVerify || startVerificationMutation.isPending || (features && !features.custom_domain)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {startVerificationMutation.isPending ? 'Starting...' : 'Start Verification'}
                </button>
                
                <button
                  onClick={handleVerifyDomain}
                  disabled={!domainToVerify || verifyDomainMutation.isPending || (features && !features.custom_domain)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifyDomainMutation.isPending ? 'Verifying...' : 'Check Verification'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: DNS Verification - Only visible to Agency subscribers */}
      {activeStep === 3 && features?.custom_domain && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">DNS Verification</h2>
          <p className="text-gray-600">Verify your custom domain by adding DNS records.</p>
          
          {brandingData.custom_domain ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">Domain Verification</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Domain to Verify
                    </label>
                    <input
                      type="text"
                      value={domainToVerify || brandingData.custom_domain}
                      onChange={(e) => {
                        setDomainToVerify(e.target.value);
                        setVerificationData(null); // Clear verification data when domain changes
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Type
                    </label>
                    <select
                      value={verificationType}
                      onChange={(e) => {
                        setVerificationType(e.target.value as 'domain' | 'sender');
                        setVerificationData(null); // Clear previous verification data
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="domain">Custom Domain</option>
                    </select>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={handleStartVerification}
                      disabled={!domainToVerify || startVerificationMutation.isPending}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {startVerificationMutation.isPending ? 'Starting...' : 'Start Verification'}
                    </button>
                    
                    <button
                      onClick={handleVerifyDomain}
                      disabled={!domainToVerify || verifyDomainMutation.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {verifyDomainMutation.isPending ? 'Verifying...' : 'Check Verification'}
                    </button>
                  </div>
                </div>
              </div>

              {/* DNS Records Display */}
              {verificationData && verificationData.dns_records && (
                <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">DNS Records to Add</h3>
                  
                  {/* DNS Provider Guides */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-md">
                    <p className="text-sm font-medium text-blue-900 mb-2">Need help? Check your DNS provider's guide:</p>
                    <div className="flex flex-wrap gap-2">
                      <a href="https://support.cloudflare.com/hc/en-us/articles/360019093151" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">Cloudflare</a>
                      <span className="text-gray-400">•</span>
                      <a href="https://www.godaddy.com/help/add-a-txt-record-19232" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">GoDaddy</a>
                      <span className="text-gray-400">•</span>
                      <a href="https://www.namecheap.com/support/knowledgebase/article.aspx/317/2237/how-do-i-add-txtspfdkimdmarc-records-for-my-domain/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">Namecheap</a>
                      <span className="text-gray-400">•</span>
                      <a href="https://help.dreamhost.com/hc/en-us/articles/360035516812-Adding-custom-DNS-records" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">DreamHost</a>
                      <span className="text-gray-400">•</span>
                      <a href="https://support.google.com/a/answer/183895" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">Google Domains</a>
                    </div>
                  </div>

                  {/* DNS Records Table */}
                  {verificationData.dns_records && (
                    Object.keys(verificationData.dns_records).length > 0 &&
                    (verificationData.dns_records.TXT?.length > 0 ||
                     verificationData.dns_records.CNAME?.length > 0 ||
                     verificationData.dns_records.DMARC?.length > 0)
                  ) ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* TXT Records */}
                        {verificationData.dns_records.TXT?.map((record: any, index: number) => {
                          const recordKey = `txt-${index}`;
                          return (
                            <tr key={recordKey}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">TXT</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{record.name || '@'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all max-w-md">{record.value}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{record.description || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => handleCopyToClipboard(record.value, recordKey)}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                  title="Copy value"
                                >
                                  {copiedIndex === recordKey ? (
                                    <>
                                      <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <ClipboardIcon className="h-4 w-4 mr-1" />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {/* CNAME Records */}
                        {verificationData.dns_records.CNAME?.map((record: any, index: number) => {
                          const recordKey = `cname-${index}`;
                          return (
                            <tr key={recordKey}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">CNAME</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{record.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all max-w-md">{record.value}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{record.description || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => handleCopyToClipboard(record.value, recordKey)}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                  title="Copy value"
                                >
                                  {copiedIndex === recordKey ? (
                                    <>
                                      <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <ClipboardIcon className="h-4 w-4 mr-1" />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {/* DMARC Records */}
                        {verificationData.dns_records.DMARC?.map((record: any, index: number) => {
                          const recordKey = `dmarc-${index}`;
                          return (
                            <tr key={recordKey}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800">TXT</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{record.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all max-w-md">{record.value}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{record.description || 'DMARC policy record'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => handleCopyToClipboard(record.value, recordKey)}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                  title="Copy value"
                                >
                                  {copiedIndex === recordKey ? (
                                    <>
                                      <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <ClipboardIcon className="h-4 w-4 mr-1" />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-md text-center text-gray-500">
                      <p>No DNS records available. Please try starting verification again.</p>
                    </div>
                  )}

                  {/* Step-by-Step Instructions */}
                  {verificationData.instructions && verificationData.instructions.length > 0 && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-md">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Step-by-Step Instructions</h4>
                      <ol className="list-decimal list-inside space-y-2">
                        {verificationData.instructions.map((instruction: string, index: number) => (
                          <li key={index} className="text-sm text-gray-700">{instruction}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Verification Token Info */}
                  {verificationData.verification_token && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        <strong>Verification Token:</strong> <span className="font-mono">{verificationData.verification_token}</span>
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Add this token to your TXT record to verify domain ownership. DNS changes may take a few minutes to propagate.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-yellow-50 p-4 rounded-md">
              <p className="text-yellow-800">Please configure a custom domain in Step 2 first.</p>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Sender Domain - Only visible to Agency subscribers */}
      {activeStep === 4 && features?.custom_domain && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Sender Domain Configuration</h2>
          <p className="text-gray-600">Configure your sender domain for email authentication (SPF, DKIM, DMARC).</p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sender Domain
            </label>
            <input
              type="text"
              value={brandingData.sender_domain || ''}
              onChange={(e) => handleInputChange('sender_domain', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="mail.example.com"
            />
            <p className="mt-1 text-xs text-gray-500">Domain used for sending emails (e.g., mail.yourcompany.com)</p>
            {brandingData.sender_domain && (
              <div className="mt-2 flex items-center">
                {getStatusIcon(brandingData.sender_domain_verified || false)}
                <span className="ml-2 text-sm text-gray-600">
                  {brandingData.sender_domain_verified ? 'Verified' : 'Not verified'}
                </span>
              </div>
            )}
          </div>

          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Sender Domain Verification</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain to Verify
                </label>
                <input
                  type="text"
                  value={domainToVerify || brandingData.sender_domain || ''}
                  onChange={(e) => setDomainToVerify(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="mail.example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Type
                </label>
                <select
                  value="sender"
                  onChange={(e) => setVerificationType(e.target.value as 'domain' | 'sender')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sender">Sender Domain</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleStartVerification}
                  disabled={!domainToVerify || startVerificationMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {startVerificationMutation.isPending ? 'Starting...' : 'Start Verification'}
                </button>
                
                <button
                  onClick={handleVerifyDomain}
                  disabled={!domainToVerify || verifyDomainMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifyDomainMutation.isPending ? 'Verifying...' : 'Check Verification'}
                </button>
              </div>
            </div>
          </div>

          {/* DNS Records Display for Sender Domain */}
          {verificationData && verificationData.dns_records && verificationType === 'sender' && (
            <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">DNS Records to Add</h3>
              
              {/* DNS Provider Guides */}
              <div className="mb-6 p-4 bg-blue-50 rounded-md">
                <p className="text-sm font-medium text-blue-900 mb-2">Need help? Check your DNS provider's guide:</p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://support.cloudflare.com/hc/en-us/articles/360019093151" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">Cloudflare</a>
                  <span className="text-gray-400">•</span>
                  <a href="https://www.godaddy.com/help/add-a-txt-record-19232" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">GoDaddy</a>
                  <span className="text-gray-400">•</span>
                  <a href="https://www.namecheap.com/support/knowledgebase/article.aspx/317/2237/how-do-i-add-txtspfdkimdmarc-records-for-my-domain/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">Namecheap</a>
                  <span className="text-gray-400">•</span>
                  <a href="https://help.dreamhost.com/hc/en-us/articles/360035516812-Adding-custom-DNS-records" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">DreamHost</a>
                  <span className="text-gray-400">•</span>
                  <a href="https://support.google.com/a/answer/183895" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline">Google Domains</a>
                </div>
              </div>

              {/* DNS Records Table */}
              {verificationData.dns_records && (
                Object.keys(verificationData.dns_records).length > 0 &&
                (verificationData.dns_records.TXT?.length > 0 ||
                 verificationData.dns_records.CNAME?.length > 0 ||
                 verificationData.dns_records.DMARC?.length > 0)
              ) ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* TXT Records */}
                    {verificationData.dns_records.TXT?.map((record: any, index: number) => {
                      const recordKey = `sender-txt-${index}`;
                      return (
                        <tr key={recordKey}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">TXT</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{record.name || '@'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all max-w-md">{record.value}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.description || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleCopyToClipboard(record.value, recordKey)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                              title="Copy value"
                            >
                              {copiedIndex === recordKey ? (
                                <>
                                  <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <ClipboardIcon className="h-4 w-4 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* CNAME Records */}
                    {verificationData.dns_records.CNAME?.map((record: any, index: number) => {
                      const recordKey = `sender-cname-${index}`;
                      return (
                        <tr key={recordKey}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">CNAME</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{record.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all max-w-md">{record.value}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.description || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleCopyToClipboard(record.value, recordKey)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                              title="Copy value"
                            >
                              {copiedIndex === recordKey ? (
                                <>
                                  <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <ClipboardIcon className="h-4 w-4 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* DMARC Records */}
                    {verificationData.dns_records.DMARC?.map((record: any, index: number) => {
                      const recordKey = `sender-dmarc-${index}`;
                      return (
                        <tr key={recordKey}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800">TXT</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">{record.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono break-all max-w-md">{record.value}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.description || 'DMARC policy record'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleCopyToClipboard(record.value, recordKey)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                              title="Copy value"
                            >
                              {copiedIndex === recordKey ? (
                                <>
                                  <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <ClipboardIcon className="h-4 w-4 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-md text-center text-gray-500">
                  <p>No DNS records available. Please try starting verification again.</p>
                </div>
              )}

              {/* Step-by-Step Instructions */}
              {verificationData.instructions && verificationData.instructions.length > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Step-by-Step Instructions</h4>
                  <ol className="list-decimal list-inside space-y-2">
                    {verificationData.instructions.map((instruction: string, index: number) => (
                      <li key={index} className="text-sm text-gray-700">{instruction}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Verification Token Info */}
              {verificationData.verification_token && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    <strong>Verification Token:</strong> <span className="font-mono">{verificationData.verification_token}</span>
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Add this token to your TXT record to verify sender domain ownership. DNS changes may take a few minutes to propagate.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 5: DNS Health Check - Only visible to Agency subscribers */}
      {activeStep === 5 && features?.custom_domain && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">DNS Health Check</h2>
              <p className="text-gray-600">Verify all DNS records are properly configured.</p>
            </div>
            <button
              onClick={() => refetchDNS()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Refresh DNS Status
            </button>
          </div>

          {dnsHealth && (
            <div className="space-y-4">
              <div className={`p-4 rounded-md ${getOverallStatusColor(dnsHealth.overall_status)}`}>
                <div className="flex items-center">
                  <CogIcon className="h-5 w-5 mr-2" />
                  <span className="font-medium">
                    Overall Status: {dnsHealth.overall_status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">SPF Record</span>
                    {getStatusIcon(dnsHealth.spf_status.verified)}
                  </div>
                  {dnsHealth.spf_status.errors.length > 0 && (
                    <div className="text-sm text-red-600">
                      {dnsHealth.spf_status.errors.join(', ')}
                    </div>
                  )}
                </div>

                <div className="border rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">DKIM Record</span>
                    {getStatusIcon(dnsHealth.dkim_status.verified)}
                  </div>
                  {dnsHealth.dkim_status.errors.length > 0 && (
                    <div className="text-sm text-red-600">
                      {dnsHealth.dkim_status.errors.join(', ')}
                    </div>
                  )}
                </div>

                <div className="border rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">DMARC Record</span>
                    {getStatusIcon(dnsHealth.dmarc_status.verified)}
                  </div>
                  {dnsHealth.dmarc_status.errors.length > 0 && (
                    <div className="text-sm text-red-600">
                      {dnsHealth.dmarc_status.errors.join(', ')}
                    </div>
                  )}
                </div>

                <div className="border rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">CNAME Record</span>
                    {getStatusIcon(dnsHealth.cname_status.verified)}
                  </div>
                  {dnsHealth.cname_status.errors.length > 0 && (
                    <div className="text-sm text-red-600">
                      {dnsHealth.cname_status.errors.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {dnsHealth.recommendations.length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-md">
                  <h3 className="font-medium text-yellow-900 mb-2">Recommendations</h3>
                  <ul className="list-disc list-inside text-sm text-yellow-800">
                    {dnsHealth.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 6: Activation - Only visible to Agency subscribers */}
      {activeStep === 6 && features?.custom_domain && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Activation & Preview</h2>
          <p className="text-gray-600">Review your configuration and activate your white-label branding.</p>
          
          {/* Activation Section */}
          <div className="bg-green-50 p-6 rounded-md border border-green-200">
            <h3 className="font-medium text-green-900 mb-4">Activate Custom Domain</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={brandingData.custom_domain_enabled || false}
                  onChange={(e) => handleInputChange('custom_domain_enabled', e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Enable custom domain and white-label branding
                </label>
              </div>
              
              {brandingData.custom_domain && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    Your custom domain: <strong>{brandingData.custom_domain}</strong>
                  </p>
                  {brandingData.domain_verified ? (
                    <div className="flex items-center text-green-700">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <span>Domain verified and ready to activate</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-yellow-700">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      <span>Domain not yet verified. Please complete DNS verification first.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Preview Section */}
          <div className="border rounded-md">
            <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Branding Preview</span>
              <div className="flex space-x-4">
                <select
                  value={previewType}
                  onChange={(e) => setPreviewType(e.target.value as 'email' | 'web' | 'sms')}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="email">Email Preview</option>
                  <option value="web">Web Preview</option>
                  <option value="sms">SMS Preview</option>
                </select>
                <button
                  onClick={handleGeneratePreview}
                  disabled={previewMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {previewMutation.isPending ? 'Generating...' : 'Generate Preview'}
                </button>
              </div>
            </div>
            <div className="p-4">
              {previewMutation.data ? (
                <div 
                  dangerouslySetInnerHTML={{ __html: previewMutation.data.preview_html }}
                />
              ) : (
                <p className="text-gray-500 text-center py-8">Click "Generate Preview" to see how your branding will look</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      {steps.length > 1 && (
        <div className="flex justify-between mt-8">
          <button
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            disabled={activeStep === 1}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex space-x-4">
            {(() => {
              // Determine max step based on plan level
              const maxStep = features?.custom_domain ? 6 : 1;
              const isLastStep = activeStep === maxStep;
              
              return (
                <>
                  {!isLastStep && (
                    <button
                      onClick={handleSave}
                      disabled={saveWizardStepMutation.isPending}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      {saveWizardStepMutation.isPending ? 'Saving...' : 'Save Step'}
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      if (isLastStep) {
                        handleSaveStep(activeStep);
                      } else {
                        setActiveStep(Math.min(maxStep, activeStep + 1));
                      }
                    }}
                    disabled={isLastStep}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLastStep ? 'Complete' : 'Next'}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
      
      {/* Progress Indicator */}
      {steps.length > 1 && wizardStatus && (
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Setup Progress</span>
            <span>{wizardStatus.progress_percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${wizardStatus.progress_percentage}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandingWizard;
